import React from 'react';
import { MuiCard, IconType, InputType, MuiSelector } from '@ace/ace-ui-web';
import FormField from '../formfield';
import { Avatar, Typography, Box, Grid, SxProps, Theme, FormControl } from '@mui/material';
import { Person, LocationOn, GroupsRounded } from '@mui/icons-material';
import { CardHeader } from '../card-header';
import { SaveButton } from '../save-button';
import { baseCardStyles } from '../common/cardStyles';
import { toast } from 'sonner';
import { handlePhoneKeyDown } from '../../utils/phoneUtils';

interface ProfileInfoCardProps {
    workdayId: string;
    userName: string;
    /** Controlled setter for the single "Name" field. When omitted the field is rendered read-only. */
    setUserName?: (value: string) => void;
    /** Client-side validation error message for the "Name" field. */
    nameError?: string;
    userEmail: string;
    email: string;
    setEmail: (value: string) => void;
    phone: string;
    onPhoneChange: (value: string) => void;
    site: string;
    team?: string;
    onTeamChange?: (value: string) => void;
    teamOptions?: Array<{ label: string; value: string }>;
    teamsLoading?: boolean;
    showTeamDropdown?: boolean;
    isAdminOrSuperUser?: boolean;
    statusChip?: React.ReactNode;
    isReadOnly?: boolean;
    hasChanges?: boolean;
    updating?: boolean;
    onSave?: () => void;
    showSaveButton?: boolean;
    additionalActions?: React.ReactNode;
    sx?: SxProps<Theme>;
    emailFieldKey?: number;
}

const ProfileInfoCard: React.FC<ProfileInfoCardProps> = ({
    workdayId,
    userName,
    setUserName,
    nameError,
    userEmail,
    email,
    setEmail,
    phone,
    onPhoneChange,
    site,
    team,
    onTeamChange,
    teamOptions = [],
    teamsLoading = false,
    showTeamDropdown = false,
    isAdminOrSuperUser = false,
    statusChip,
    isReadOnly = false,
    hasChanges = false,
    updating = false,
    onSave,
    showSaveButton = true,
    additionalActions,
    sx,
    emailFieldKey,
}) => {
    // When isReadOnly is true, it means HR/Manager is viewing
    // In that case, email and phone should be read-only, but team should be editable
    const isEmailPhoneEditable = !isReadOnly;
    const isTeamEditable = true; // Team is always editable (both for users and HR/Managers)
    // Name is editable only when we have a setter AND we're not in read-only mode.
    const isNameEditable = !isReadOnly && typeof setUserName === 'function';

    return (
        <MuiCard
            borderRadius={5}
            elevation={1}
            padding={2}
            variant="elevation"
            sx={{ ...baseCardStyles, ...sx }}
        >
            <CardHeader
                title="Profile Information"
                subtitle={isReadOnly ? "View employee details" : "Update your personal details"}
                action={statusChip}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 3, marginBottom: 2 }}>
                <Avatar sx={{ bgcolor: '#cc0000', width: 48, height: 48 }}>
                    <Person sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: -0.5 }}>
                        {userName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {userEmail}
                    </Typography>
                </Box>
            </Box>
            
            <Box sx={{ maxWidth: '600px' }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormField label="Workday ID" value={workdayId} muiIcon={Person} readOnly={true} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        {/*
                          Single editable "Name" field.
                          Backend stores as one column; useProfileForm splits it
                          into firstName/lastName on submit.
                        */}
                        <FormField
                            label="Name"
                            value={userName}
                            onChange={(e) => setUserName?.(e.target.value)}
                            muiIcon={Person}
                            readOnly={!isNameEditable}
                            error={isNameEditable ? (nameError ?? undefined) : undefined}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormField
                            key={emailFieldKey}
                            label="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            type={InputType.Email}
                            icon={IconType.envelope}
                            readOnly={!isEmailPhoneEditable}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormField
                            label="Phone Number"
                            value={phone}
                            onKeyDown={handlePhoneKeyDown}
                            onChange={(e) => {
                                const raw = e.target.value.replaceAll(/\D/g, "");
                                if (raw.length > 10) {
                                    toast.warning("Phone number cannot exceed 10 digits.", { id: "phone-limit" });
                                }
                                const numericValue = raw.slice(0, 10);
                                let formatted = numericValue;
                                if (numericValue.length > 6) {
                                    formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3, 6)}-${numericValue.slice(6)}`;
                                } else if (numericValue.length > 3) {
                                    formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`;
                                }
                                onPhoneChange(formatted);
                            }}
                            type={InputType.Phone}
                            icon={IconType.phone}
                            iconSize={{ height: 20, width: 15 }}
                            readOnly={!isEmailPhoneEditable}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormField label="Site" value={site} muiIcon={LocationOn} readOnly={true} />
                    </Grid>
                    {/* Team Dropdown - Show for regular users (not admin/superuser), always editable */}
                    {showTeamDropdown && !isAdminOrSuperUser && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <GroupsRounded sx={{ fontSize: 15, color: 'gray' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                        Team
                                    </Typography>
                                </Box>
                                <FormControl fullWidth>
                                    <MuiSelector
                                        placeholder="Select your team"
                                        sx={{ 
                                            height: "2.75rem", 
                                            borderRadius: "calc(var(--radius) - 0.125rem)"
                                        }}
                                        onChange={onTeamChange || (() => {})}
                                        value={team || ''}
                                        disabled={teamsLoading || !isTeamEditable}
                                        options={teamOptions}
                                    />
                                </FormControl>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {additionalActions}

                {showSaveButton && onSave && (
                    <SaveButton
                        label="Save Changes"
                        disabled={updating || !hasChanges}
                        updating={updating}
                        onSave={onSave}
                    />
                )}
            </Box>
        </MuiCard>
    );
};

export default ProfileInfoCard;